<template>
  <a-drawer
    :visible.sync="internalVisible"
    title="Edit Financing Terms"
    direction="rtl"
    width="70%"
    :closable="true"
    :maskClosable="true"
    @close="handleClose"
    custom-class="custom-drawer"
  >
    <!-- Remove footer slot -->
    <a-row :gutter="[16, 16]">
      <a-col :span="14">
        <div class="form-section">
          <a-form :model="form" layout="vertical">
            <a-row :gutter="[16, 16]">
              <h1 class="card-details-header">Invoice Details</h1>
              <a-col :span="12">
                <a-card class="card-meta">
                  <a-card-meta
                    title="Invoice Value"
                    :description="`Amount: ${formatAmount(requestData.invoiceValue)}`"
                  />
                </a-card>
              </a-col>
              <a-col :span="12">
                <a-card class="card-meta">
                  <a-card-meta
                    title="Requested Amount"
                    :description="`Amount: ${formatAmount(requestData.requestedAmount)}`"
                  />
                </a-card>
              </a-col>
            </a-row>

            <a-row :gutter="[16, 16]">
              <a-col :span="12">
                <a-form-item label="Financing Fee (%)">
                  <a-input-number
                    v-model="form.fee"
                    :min="0"
                    :max="100"
                    :step="0.1"
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>

              <a-col :span="12">
                <a-form-item label="Calculated Fee Amount">
                  <a-input
                    :value="calculatedFeeDisplay"
                    disabled
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-form-item label="Net Payout to Seller">
              <a-input :value="netPayoutDisplay" disabled style="width: 100%" />
            </a-form-item>

            <a-row :gutter="[16, 16]">
              <a-col :span="12">
                <a-form-item label="Repayment Term (Days)">
                  <a-select
                    v-model="form.repaymentTerm"
                    placeholder="Select term"
                    style="width: 100%"
                    @change="calculateRepaymentDueDate"
                  >
                    <a-select-option :value="30">30 Days</a-select-option>
                    <a-select-option :value="60">60 Days</a-select-option>
                    <a-select-option :value="90">90 Days</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>

              <a-col :span="12">
                <a-form-item label="Repayment Due Date">
                  <a-date-picker
                    v-model="form.repaymentDueDate"
                    format="YYYY-MM-DD"
                    valueFormat="YYYY-MM-DD"
                    
                    style="width: 100%"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-form-item>
              <div class="flex-label-switch">
                <label class="switch-label">Allow Partial Repayment?</label>
                <a-switch
                  v-model="form.allowPartialRepayment"
                  checked-children="Yes"
                  un-checked-children="No"
                />
              </div>
            </a-form-item>

            <a-form-item label="Comments / Notes">
              <a-textarea
                v-model="form.comments"
                placeholder="Enter any relevant notes or comments about the terms..."
                :rows="4"
                style="width: 100%"
              />
            </a-form-item>
          </a-form>
        </div>
      </a-col>

      <a-col :span="10">
        <a-col :span="24">
          <a-card
            style="
              background-color: #72c5871c;
              border-radius: 8px;
              border: none;
            "
          >
            <h1 class="card-details-header">Financing Terms Summary</h1>
            <p>
              <strong>Original Invoice Value:</strong>
              {{ formatAmount(invoiceValue) }}
            </p>
            <p>
              <strong>Financing Fee:</strong> {{ form.fee }}% ({{ formatAmount(calculatedFee) }})
            </p>
            <p>
              <strong>Net Disbursement to Seller:</strong>
              {{ formatAmount(netPayout) }}
            </p>
            <p>
              <strong>Payment Due Term:</strong>
              {{ form.repaymentTerm || "-" }} days
            </p>
            <p>
              <strong>Repayment Due Date:</strong>
              {{ form.repaymentDueDate || "-" }}
            </p>
          </a-card>
        </a-col>
        <a-col :span="24">
          <a-alert
            v-if="form.fee < 2.5"
            type="error"
            message="The selected fee is below the recommended minimum of 2.5% for this risk profile."
            show-icon
            style="margin-bottom: 16px"
          />
        </a-col>
      </a-col>
    </a-row>

    <div class="drawer-footer">
      <a-button @click="handleClose">Cancel Changes</a-button>
      <a-button type="primary" @click="handleSave" style="background: #26a246">
        <i class="fa fa-check" style="margin-right: 5px; border: none"></i>
        Apply New Terms
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
import { formatAmount } from "../../utils";

export default {
  name: "EditTermsDrawer",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    requestData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      internalVisible: this.visible,
      form: {
        fee: 0,
        calculatedFee: 0,
        netPayout: 0,
        repaymentTerm: null,
        repaymentDueDate: "",
        paymentSchedule: "",
        allowPartialRepayment: false,
        comments: "", // 🆕 Add comments f
      },
    };
  },
  computed: {
    invoiceValue() {
      return parseFloat(this.requestData.invoiceValue) || 0;
    },
    calculatedFee() {
      return (this.form.fee / 100) * this.invoiceValue;
    },
    netPayout() {
      return this.invoiceValue - this.calculatedFee;
    },
    calculatedFeeDisplay() {
      return this.formatAmount(this.calculatedFee);
    },
    netPayoutDisplay() {
      return this.formatAmount(this.netPayout);
    },
  },
  watch: {
    visible(val) {
      this.internalVisible = val;
      if (val) {
        this.resetForm();
      }
    },
    internalVisible(val) {
      this.$emit("update:visible", val);
    },
    "form.fee"() {
      this.syncCalculatedFields();
    },
    "form.repaymentTerm"() {
      this.calculateRepaymentDueDate();
    },
  },
  methods: {
    formatAmount,
    calculateRepaymentDueDate() {
      if (!this.form.repaymentTerm) return;
      const today = new Date();
      today.setDate(today.getDate() + this.form.repaymentTerm);
      this.form.repaymentDueDate = today.toISOString().slice(0, 10);
      this.syncCalculatedFields();
    },
    resetForm() {
      this.form = {
        fee: this.requestData.fee !== undefined ? this.requestData.fee : 0,
        repaymentTerm: this.requestData.repaymentTerm || null,
        repaymentDueDate: this.requestData.repaymentDate || "",
        paymentSchedule: this.requestData.paymentSchedule || "",
        allowPartialRepayment: this.requestData.allowPartialRepayment || false,
        calculatedFee: 0,
        netPayout: this.requestData.requestedAmount,
        comments: this.requestData.comments || "",
      };
    },
    handleClose() {
      this.internalVisible = false;
    },

    handleSave() {
      const updatedTerms = {
        fee: this.form.fee,
        repaymentDueDate: this.form.repaymentDueDate ? this.form.repaymentDueDate.format('YYYY-MM-DD') : null,
        paymentSchedule: this.form.paymentSchedule,
        netPayout: this.netPayout,
        calculatedFee: this.calculatedFee,
        repaymentTerm: this.form.repaymentTerm,
        allowPartialRepayment: this.form.allowPartialRepayment,
        comments: this.form.comments
      };

      this.$emit("update-terms", updatedTerms);

      // Show success notification
      this.$notification.success({
        message: "Terms Updated",
        description: "Financing terms have been successfully updated.",
        placement: "topRight",
        duration: 3,
      });

      this.handleClose();
    },

    syncCalculatedFields() {
      this.form.calculatedFee = this.calculatedFee;
      this.form.netPayout = this.netPayout;
    },
  },
  
};
</script>
<style scoped>
.form-section {
  padding: 1rem 0;
}
.drawer-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
  gap: 8px;
}
.card-meta {
  border-radius: 8px;
  background: #f9fafb;
}
.card-details-header {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
}

.ant-card-meta-description {
  font-size: 14px;
  color: #666;
}

.flex-label-switch {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-label {
  font-weight: 500;
  color: #333;
}

.ant-notification {
  /* Base notification styles */
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.ant-notification-notice {
  /* Individual notification item */
  padding: 16px !important;
  background: #fff !important;
}

.ant-notification-notice-success {
  /* Success notification specific styles */
  border-left: 4px solid #26a246 !important;
}

.ant-notification-message {
  /* Message text styles */
  font-size: 16px !important;
  color: #333 !important;
}

.ant-notification-description {
  /* Description text styles */
  font-size: 14px !important;
  color: #666 !important;
  margin-top: 8px !important;
}

.ant-notification-close {
  /* Close button styles */
  top: 12px !important;
  right: 12px !important;
  color: #999 !important;
}

.ant-notification-close:hover {
  color: #666 !important;
}
</style>
